import type { ReactNode, RefObject } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import { useTracking } from "react-tracking";

import type {
  IBuiSplitViewHandleProps,
  IBuiSplitViewPanelProps,
  IBuiSplitViewProps,
  ImperativePanelHandle,
} from "@btnet/blueprint-components";
import { cn } from "@btnet/utilities-core";

// Matches the "md" breakpoint in Clients.App's styles/antTheme/Variables (Screen.md).
// Inlined here because that module isn't reachable from this package.
const RESPONSIVE_COLLAPSE_BREAKPOINT_PX = 768;

export type BTSplitViewMode = "list" | "split" | "detail";

/**
 * Props for BTSplitView - a curated subset of BuiSplitView props
 * with mode switching and responsive collapse.
 *
 * Uses props-as-slots (list, detail) instead of compound children
 * per FE COP decision.
 *
 * Default and minimum sizes are in pixels (matching how content-based constraints
 * are specified in design). Maximum is a percentage (matching "50% screen width" AC).
 * The hook converts pixels to percentages internally using the container width.
 *
 * Types are explicit (not derived from BuiSplitView) so that react-docgen-typescript
 * can resolve them for Storybook controls without traversing workspace packages.
 *
 * Defined here (not in BTSplitView.tsx) so this hook doesn't import from the
 * component that imports the hook — dependency-cruiser's no-circular rule flags
 * that cycle even for type-only imports.
 */
export interface IBTSplitViewProps {
  /** Additional CSS class names. */
  className?: string;

  /** Resize direction. @default "horizontal" */
  direction?: "horizontal" | "vertical";

  /** Display mode: which panels are visible */
  mode: BTSplitViewMode;

  /** Content for the primary/list pane */
  list: ReactNode;

  /** Content for the detail/secondary pane */
  detail: ReactNode;

  /** Required test identifier */
  testid: string;

  /**
   * Accessible name for landmark labels.
   * List panel gets `aria-label="{name}"`, detail panel gets `aria-label="{name} details"`.
   * @example "To-Dos" | "Documents" | "Photos"
   */
  name: string;

  /** Default detail panel width in pixels. @default 400 */
  defaultDetailWidth?: number;

  /** Minimum detail panel width in pixels. @default 320 */
  minDetailWidth?: number;

  /** Maximum detail panel size as a percentage (0-100). @default 50 */
  maxDetailSize?: number;

  /** Auto-collapse to detail-only when container < 768px. @default true */
  responsiveCollapse?: boolean;

  /** Called when the detail panel size changes during/after resize. Size is a percentage (0-100). */
  onDetailResize?: (size: number, prevSize: number | undefined) => void;

  /**
   * Enables layout persistence via localStorage.
   * When provided, the user's panel sizes are saved and restored across page loads.
   * Use a unique, route-specific value (e.g., "tasks-panel", "schedule-wde").
   */
  persistenceId?: string;

  /**
   * Allows the detail panel to become collapsed by dragging the handle past
   * the minimum width. Programmatic mode switching (mode="list") still works.
   * @default false
   */
  allowDragCollapse?: boolean;
}

export interface IBTSplitViewReturn {
  /** Attach to the outer container div for ResizeObserver measurement. */
  containerRef: RefObject<HTMLDivElement>;
  /** True after the container has been measured. Panels should not render until this is true. */
  isMeasured: boolean;
  /** Class name for the outer container div. */
  containerClassName: string;
  /** Props for the BuiSplitView group (PanelGroup). */
  groupProps: Pick<
    IBuiSplitViewProps,
    "testid" | "direction" | "autoSaveId" | "className"
  >;
  /** Props for the list (primary) panel. */
  listPanelProps: Pick<
    IBuiSplitViewPanelProps,
    | "testid"
    | "id"
    | "role"
    | "aria-label"
    | "defaultSize"
    | "collapsible"
    | "collapsedSize"
  > & { ref: RefObject<ImperativePanelHandle> };
  /** Props for the detail (secondary) panel. */
  detailPanelProps: Pick<
    IBuiSplitViewPanelProps,
    | "testid"
    | "id"
    | "role"
    | "aria-label"
    | "defaultSize"
    | "minSize"
    | "maxSize"
    | "collapsible"
    | "collapsedSize"
    | "onResize"
  > & { ref: RefObject<ImperativePanelHandle> };
  /** Props for the resize handle between panels. */
  handleProps: Pick<
    IBuiSplitViewHandleProps,
    "testid" | "id" | "disabled" | "className"
  >;
  /**
   * True when only the detail pane is showing (effective mode "detail" — an
   * explicit `mode="detail"` or a responsive collapse below `Screen.md`).
   * Provided to the detail slot via `DetailFullWidthContext` so its content
   * can drop chrome that only makes sense alongside the list.
   */
  isDetailFullWidth: boolean;
}

/** Convert a pixel value to a percentage of the container, clamped to [0, max]. */
function pxToPercent(px: number, containerWidth: number, max = 100): number {
  if (containerWidth <= 0) {
    return 0;
  }
  return Math.min((px / containerWidth) * 100, max);
}

/** Grab the default percents for widths for the list and detail content, so that on initial paint the list & detail have actual widths.
 * Without defining this, the defaults are 0%, which can negatively affect scroll behavior.
 */
const getDefaultSizePercents = (
  effectiveMode: BTSplitViewMode,
  defaultDetailPercent: number,
): { listDefaultSizePercent: number; detailDefaultSizePercent: number } => {
  if (effectiveMode === "detail") {
    return { listDefaultSizePercent: 0, detailDefaultSizePercent: 100 };
  }

  if (effectiveMode === "list") {
    return { listDefaultSizePercent: 100, detailDefaultSizePercent: 0 };
  }

  return {
    listDefaultSizePercent: 100 - defaultDetailPercent,
    detailDefaultSizePercent: defaultDetailPercent,
  };
};

/**
 * Hook that manages BTSplitView state: mode switching, responsive collapse,
 * pixel→percent conversion, prop mapping, and analytics tracking.
 *
 * Returns props for BuiSplitView, its panels, handle, and a container ref
 * for the ResizeObserver.
 *
 * Entity variants can reuse this hook to get all business logic without
 * forking the component.
 */
export function useBTSplitView(props: IBTSplitViewProps): IBTSplitViewReturn {
  const {
    mode,
    testid,
    name,
    className,
    direction = "horizontal",
    defaultDetailWidth = 400,
    minDetailWidth = 320,
    maxDetailSize = 50,
    responsiveCollapse = true,
    allowDragCollapse = false,
    onDetailResize,
    persistenceId,
  } = props;

  const listRef = useRef<ImperativePanelHandle>(null);
  const detailRef = useRef<ImperativePanelHandle>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [isResponsiveCollapsed, setIsResponsiveCollapsed] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const tracking = useTracking<Record<string, unknown>>({
    element: "SplitView",
    uniqueId: testid,
  });

  // Pixel → percentage conversions (recomputed on every container resize).
  // Both are clamped to maxDetailSize: default so the panel never opens wider than max,
  // min so the constraints stay valid (minSize <= maxSize). When the container is too
  // narrow for the pixel minimum, the min clamps to max — the panel is effectively
  // fixed-width until the container grows. collapse() still works because
  // collapsedSize: 0 is a special state that bypasses minSize.
  const defaultDetailPercent = pxToPercent(
    defaultDetailWidth,
    containerWidth,
    maxDetailSize,
  );
  const minDetailPercent = pxToPercent(
    minDetailWidth,
    containerWidth,
    maxDetailSize,
  );

  // Store in a ref so applyVisualMode can read the latest value without
  // being recreated on every container resize.
  const defaultDetailPercentRef = useRef(defaultDetailPercent);
  useEffect(() => {
    defaultDetailPercentRef.current = defaultDetailPercent;
  }, [defaultDetailPercent]);

  // The visual mode accounts for responsive collapse:
  // when the container is narrow and mode is "split", show only detail.
  const effectiveMode: BTSplitViewMode =
    isResponsiveCollapsed && mode === "split" ? "detail" : mode;

  const isMeasured = containerWidth > 0;
  const isSplit = effectiveMode === "split";

  // Initial panel sizes are derived from the effective mode so the very first commit
  // already paints the correct geometry. react-resizable-panels only normalizes its
  // internal layout in a post-children layout effect; until then each panel falls back
  // to its own defaultSize (or "1" if absent). Native autoFocus and any content that
  // measures scrollHeight run during that first commit, so the panel a focused input
  // lives in must already have its final width — otherwise focus scrolls against a
  // mis-sized layout. Sizes only seed the initial layout; runtime changes go through
  // applyVisualMode. When persistenceId is set the library restores saved sizes and
  // ignores these defaults.
  const defaultSizes = getDefaultSizePercents(
    effectiveMode,
    defaultDetailPercent,
  );
  const listDefaultSize = defaultSizes.listDefaultSizePercent;
  const detailDefaultSize = defaultSizes.detailDefaultSizePercent;

  // Applies the visual mode by collapsing/expanding panels imperatively.
  // Used for mode changes after mount; expand() restores the pre-collapse size.
  // The initial layout is established declaratively via defaultSize, so these calls
  // are no-ops on first mount and never overwrite a persisted layout.
  const applyVisualMode = useCallback((visualMode: BTSplitViewMode) => {
    const list = listRef.current;
    const detail = detailRef.current;
    if (!list || !detail) {
      return;
    }

    switch (visualMode) {
      case "list":
        detail.collapse();
        list.expand();
        break;
      case "split":
        list.expand();
        detail.expand(defaultDetailPercentRef.current);
        break;
      case "detail":
        list.collapse();
        detail.expand(defaultDetailPercentRef.current);
        break;
    }
  }, []);

  // Apply visual mode whenever the effective mode changes or panels first mount.
  // isMeasured is included because panels aren't rendered until measurement completes
  // (two-pass render), so the refs are null on the first effect run.
  useEffect(() => {
    applyVisualMode(effectiveMode);
  }, [effectiveMode, applyVisualMode, isMeasured]);

  // Reveal layout after first render (removes visibility:hidden initializing class).
  useEffect(() => {
    setIsInitialized(true);
  }, []);

  // Track mode changes
  const prevModeRef = useRef(mode);
  useEffect(() => {
    if (prevModeRef.current !== mode) {
      tracking.trackEvent({ event: "CollapseToggled" });
      prevModeRef.current = mode;
    }
  }, [mode, tracking]);

  // Container measurement via ResizeObserver.
  // Always runs (needed for pixel→percent conversion), with optional responsive collapse.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) {
        return;
      }

      const width = entry.contentRect.width;
      setContainerWidth(width);
      if (responsiveCollapse) {
        setIsResponsiveCollapsed(width < RESPONSIVE_COLLAPSE_BREAKPOINT_PX);
      }
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, [responsiveCollapse]);

  const handleDetailResize = useCallback(
    (size: number, prevSize: number | undefined) => {
      if (
        !allowDragCollapse &&
        effectiveMode === "split" &&
        size === 0 &&
        minDetailPercent > 0
      ) {
        detailRef.current?.resize(minDetailPercent);
      }
      onDetailResize?.(size, prevSize);
    },
    [allowDragCollapse, effectiveMode, minDetailPercent, onDetailResize],
  );

  return {
    containerRef,
    isMeasured,
    containerClassName: cn("bt-splitview-container", {
      "bt-splitview--initializing": !isInitialized,
    }),
    groupProps: {
      testid,
      direction,
      autoSaveId: persistenceId ?? null,
      className: cn("bt-splitview", className),
    },
    listPanelProps: {
      testid: `${testid}-list`,
      id: `${testid}-list`,
      ref: listRef,
      role: "region" as const,
      "aria-label": name,
      defaultSize: listDefaultSize,
      collapsible: true,
      collapsedSize: 0,
    },
    detailPanelProps: {
      testid: `${testid}-detail`,
      id: `${testid}-detail`,
      ref: detailRef,
      role: "complementary" as const,
      "aria-label": `${name} details`,
      defaultSize: detailDefaultSize,
      minSize: isSplit ? minDetailPercent : 0,
      maxSize: isSplit ? maxDetailSize : 100,
      collapsible: true,
      collapsedSize: 0,
      onResize: handleDetailResize,
    },
    handleProps: {
      testid: `${testid}-handle`,
      id: `${testid}-handle`,
      disabled: !isSplit,
      className: cn({
        "bui-splitview-handle--hidden": !isSplit,
      }),
    },
    isDetailFullWidth: effectiveMode === "detail",
  };
}
